import React, { useRef, useState } from "react";
import { fileUrl } from "../lib/api";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

// Swipeable photo strip used inside catalog cards. Lets the user browse all
// photos of an artifact directly from the list (swipe on touch, arrows on
// hover) without opening the detail page. Safe inside <Link>/<button> cards:
// arrows/dots are spans and clicks are stopped from bubbling to the card.
export const CardPhotoCarousel = ({ photos = [], alt = "Reperto", testId }) => {
  const [idx, setIdx] = useState(0);
  const touch = useRef({ x: 0, y: 0, swiped: false });
  const many = photos.length > 1;

  const go = (dir) => setIdx((i) => (i + dir + photos.length) % photos.length);

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, swiped: false };
  };
  const onTouchEnd = (e) => {
    if (!many) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      touch.current.swiped = true;
      go(dx < 0 ? 1 : -1);
    }
  };
  const onClickCapture = (e) => {
    if (touch.current.swiped) {
      e.preventDefault();
      e.stopPropagation();
      touch.current.swiped = false;
    }
  };
  const arrow = (e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    go(dir);
  };

  return (
    <div
      className="aspect-square w-full overflow-hidden bg-[#E5E0D8] relative group/photo select-none"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClickCapture={onClickCapture}
      data-testid={testId}
    >
      {photos.length > 0 ? (
        <div
          className="flex h-full w-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {photos.map((p, i) => (
            <img
              key={p}
              src={fileUrl(p)}
              alt={`${alt} — foto ${i + 1}`}
              loading="lazy"
              draggable={false}
              className="w-full h-full object-cover shrink-0"
            />
          ))}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#5C635A]">Senza foto</div>
      )}

      {many && (
        <>
          <span
            role="button"
            aria-label="Foto precedente"
            onClick={(e) => arrow(e, -1)}
            data-testid={testId ? `${testId}-prev` : undefined}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center bg-black/45 hover:bg-black/70 text-white rounded-full h-8 w-8 cursor-pointer opacity-0 group-hover/photo:opacity-100 max-md:opacity-80 transition-opacity"
          >
            <CaretLeft size={16} weight="bold" />
          </span>
          <span
            role="button"
            aria-label="Foto successiva"
            onClick={(e) => arrow(e, 1)}
            data-testid={testId ? `${testId}-next` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center bg-black/45 hover:bg-black/70 text-white rounded-full h-8 w-8 cursor-pointer opacity-0 group-hover/photo:opacity-100 max-md:opacity-80 transition-opacity"
          >
            <CaretRight size={16} weight="bold" />
          </span>

          <span className="absolute top-3 right-3 z-10 bg-black/50 text-white font-mono text-[11px] px-2 py-0.5 rounded-sm">
            {idx + 1}/{photos.length}
          </span>

          <span className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
            {photos.map((_, i) => (
              <span
                key={i}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIdx(i);
                }}
                className={`h-1.5 rounded-full cursor-pointer transition-all ${
                  i === idx ? "w-4 bg-white" : "w-1.5 bg-white/55"
                }`}
              />
            ))}
          </span>
        </>
      )}
    </div>
  );
};
